import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { TourType, Translation, initialTourType } from '../models/tourType'
import { getAll, createOne, deleteOne, getOneById, patchOne } from '../../../../services/common_requests'
import { KTSVG } from '../../../../../_dashboard/helpers/components/KTSVG'
import { toast } from 'react-toastify'
import { Tooltip, OverlayTrigger, Form } from 'react-bootstrap'
import { AVAILABLE_LANGUAGES } from '../constants'

const TourTypesTab: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [tourTypes, setTourTypes] = useState<TourType[]>([])
  const [editingTourType, setEditingTourType] = useState<TourType | null>(null)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [tourTypeToDelete, setTourTypeToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const pageSize = 1000

  const loadTourTypes = async () => {
    try {
      setLoading(true)
      const response = await getAll({page, size: pageSize}, '/tour-types')
      setTourTypes(response.data || [])
      setTotalPages(response.payload?.pagination?.totalPages || 0)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_TYPE.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTourTypes()
    // eslint-disable-next-line
  }, [page])

  const tourTypeSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    description: Yup.string().required('Description is required'),
    status: Yup.string().oneOf(['active', 'inactive']).required('Status is required'),
    translations: Yup.array().of(
      Yup.object().shape({
        language: Yup.string().required('Language is required'),
        title: Yup.string().required('Title is required'),
        description: Yup.string().required('Description is required'),
      })
    ),
  })

  const formik = useFormik({
    initialValues: editingTourType || initialTourType,
    validationSchema: tourTypeSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (editingTourType?._id) {
          // Remove _id and __v from the main object and translations
          const { _id, __v, translations, ...rest } = values as any
          const cleanTranslations = (translations || []).map((t: any) => {
            const { _id, __v, ...tRest } = t
            return tRest
          })
          const cleanValues = { ...rest, translations: cleanTranslations }
          await patchOne(cleanValues, `/tour-types/${editingTourType._id}`)
          toast.success(intl.formatMessage({ id: 'TOUR_TYPE.SUCCESS.UPDATE' }))
        } else {
          await createOne(values, '/tour-types')
          toast.success(intl.formatMessage({ id: 'TOUR_TYPE.SUCCESS.CREATE' }))
        }
        resetForm()
        setEditingTourType(null)
        setShowModal(false)
        loadTourTypes()
      } catch (error) {
        toast.error(intl.formatMessage({ id: editingTourType?._id ? 'TOUR_TYPE.ERROR.UPDATE' : 'TOUR_TYPE.ERROR.CREATE' }))
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleDelete = async () => {
    if (!tourTypeToDelete) return
    try {
      setIsDeleting(true)
      await deleteOne(tourTypeToDelete, '/tour-types')
      toast.success(intl.formatMessage({ id: 'TOUR_TYPE.SUCCESS.DELETE' }))
      loadTourTypes()
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_TYPE.ERROR.DELETE' }))
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
      setTourTypeToDelete(null)
    }
  }

  const handleEdit = async (tourType: TourType) => {
    try {
      setLoading(true)
      const response = await getOneById(tourType._id!, '/tour-types')
      setEditingTourType(response)
      formik.setValues(response)
      setShowModal(true)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_TYPE.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditingTourType(null)
    formik.resetForm()
    setShowModal(false)
  }

  const handleAddNew = () => {
    setEditingTourType(null)
    formik.resetForm()
    setShowModal(true)
  }

  const handleAddTranslation = () => {
    const currentTranslations = formik.values.translations || []
    const usedLanguages = new Set(currentTranslations.map(t => t.language))
    const availableLanguage = AVAILABLE_LANGUAGES.find(lang => !usedLanguages.has(lang))
    if (availableLanguage) {
      formik.setFieldValue('translations', [
        ...currentTranslations,
        { language: availableLanguage, title: '', description: '' }
      ])
    } else {
      toast.warning(intl.formatMessage({ id: 'TOUR_TYPE.ERROR.NO_AVAILABLE_LANGUAGES' }))
    }
  }

  const handleRemoveTranslation = (index: number) => {
    const currentTranslations = [...formik.values.translations]
    currentTranslations.splice(index, 1)
    formik.setFieldValue('translations', currentTranslations)
  }

  return (
    <div className='card card-custom'>
      {/* Header with Add Button */}
      <div className='card-header border-0 pt-6'>
        <div className='card-title'></div>
        <div className='card-toolbar'>
          <div className='d-flex justify-content-end'>
            <button type='button' className='btn btn-primary' onClick={handleAddNew}>
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
              {intl.formatMessage({ id: 'TOUR_TYPE.ADD_NEW' })}
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className='card-body pt-0'>
        {loading ? (
          <div className='d-flex justify-content-center align-items-center min-h-300px'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className='table-responsive'>
              <table className='table align-middle table-row-dashed fs-6 gy-5'>
                <thead>
                  <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                    <th>{intl.formatMessage({ id: 'TOUR_TYPE.TITLE' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_TYPE.DESCRIPTION' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_TYPE.TRANSLATIONS' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_TYPE.STATUS' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_TYPE.ACTIONS' })}</th>
                  </tr>
                </thead>
                <tbody className='text-gray-600 fw-semibold'>
                  {tourTypes.map((tourType) => (
                    <tr key={tourType._id}>
                      <td>{tourType.title}</td>
                      <td>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{tourType.description}</Tooltip>}
                        >
                          <span className='text-truncate d-inline-block' style={{ maxWidth: '200px' }}>
                            {tourType.description}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td>
                        <div className='d-flex flex-wrap gap-2'>
                          {(tourType.translations || []).map((translation) => (
                            <span key={translation.language} className='badge badge-light-primary'>
                              {translation.language.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge badge-light-${
                            tourType.status === 'active' ? 'success' : 'danger'
                          } fw-bold`}
                        >
                          {tourType.status}
                        </span>
                      </td>
                      <td>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.EDIT'})}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-primary btn-sm me-1'
                            onClick={() => handleEdit(tourType)}
                          >
                            <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.DELETE'})}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-danger btn-sm'
                            onClick={() => {
                              if (tourType._id) {
                                setTourTypeToDelete(tourType._id)
                                setShowDeleteConfirm(true)
                              }
                            }}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className='d-flex justify-content-between align-items-center flex-wrap'>
                <div className='d-flex flex-wrap py-2 mr-3'>
                  <button
                    className='btn btn-light btn-active-light-primary me-2'
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.PREVIOUS' })}
                  </button>
                  <button
                    className='btn btn-light btn-active-light-primary'
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.NEXT' })}
                  </button>
                </div>
                <div className='d-flex align-items-center py-3'>
                  <span className='text-muted'>
                    {intl.formatMessage(
                      { id: 'PAGINATION.INFO' },
                      {
                        page: page + 1,
                        total: totalPages,
                      }
                    )}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showModal && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 1050}}
        >
          <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable mw-650px'>
            <div className='modal-content border-0 rounded-4 shadow-lg'>
              <div className='modal-header border-0 pt-6 px-8 pb-2'>
                <h5 className='modal-title fs-3 fw-bold text-gray-900'>
                  {editingTourType ? intl.formatMessage({id: 'TOUR_TYPE.EDIT'}) : intl.formatMessage({id: 'TOUR_TYPE.ADD_NEW'})}
                </h5>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={handleCancel}
                >
                  <i className='bi bi-x-lg fs-3' />
                </button>
              </div>
              <Form onSubmit={formik.handleSubmit}>
                <div className='modal-body px-8 py-6'>
                  <div className='mb-5'>
                    <label className='form-label required'>{intl.formatMessage({id: 'TOUR_TYPE.TITLE'})}</label>
                    <input
                      type='text'
                      className={`form-control ${formik.touched.title && formik.errors.title ? 'is-invalid' : ''}`}
                      {...formik.getFieldProps('title')}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <div className='invalid-feedback'>{formik.errors.title}</div>
                    )}
                  </div>

                  <div className='mb-5'>
                    <label className='form-label required'>{intl.formatMessage({id: 'TOUR_TYPE.DESCRIPTION'})}</label>
                    <textarea
                      className={`form-control ${formik.touched.description && formik.errors.description ? 'is-invalid' : ''}`}
                      rows={3}
                      {...formik.getFieldProps('description')}
                    />
                    {formik.touched.description && formik.errors.description && (
                      <div className='invalid-feedback'>{formik.errors.description}</div>
                    )}
                  </div>

                  <div className='mb-5'>
                    <label className='form-label required'>{intl.formatMessage({id: 'TOUR_TYPE.STATUS'})}</label>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        checked={formik.values.status === 'active'}
                        onChange={(e) => formik.setFieldValue('status', e.target.checked ? 'active' : 'inactive')}
                      />
                      <label className='form-check-label'>
                        {formik.values.status === 'active' ? 'Active' : 'Inactive'}
                      </label>
                    </div>
                  </div>

                  {/* Translations Section */}
                  <div className='mb-5'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <label className='form-label'>{intl.formatMessage({id: 'TOUR_TYPE.TRANSLATIONS'})}</label>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={handleAddTranslation}
                      >
                        <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                        {intl.formatMessage({id: 'TOUR_TYPE.ADD_TRANSLATION'})}
                      </button>
                    </div>

                    {(formik.values.translations || []).map((translation, index) => (
                      <div key={index} className='card card-custom mb-5'>
                        <div className='card-header'>
                          <div className='card-title'>
                            <div className='d-flex align-items-center'>
                              <select
                                className='form-select form-select-solid me-3'
                                value={translation.language}
                                onChange={(e) => {
                                  const newTranslations = [...formik.values.translations]
                                  newTranslations[index].language = e.target.value
                                  formik.setFieldValue('translations', newTranslations)
                                }}
                              >
                                {AVAILABLE_LANGUAGES.map((lang) => (
                                  <option
                                    key={lang}
                                    value={lang}
                                    disabled={formik.values.translations.some(
                                      (t, i) => i !== index && t.language === lang
                                    )}
                                  >
                                    {lang.toUpperCase()}
                                  </option>
                                ))}
                              </select>
                              <button
                                type='button'
                                className='btn btn-icon btn-light-danger btn-sm'
                                onClick={() => handleRemoveTranslation(index)}
                              >
                                <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className='card-body'>
                          <div className='mb-5'>
                            <label className='form-label required'>{intl.formatMessage({id: 'TOUR_TYPE.TRANSLATION_TITLE'})}</label>
                            <input
                              type='text'
                              className='form-control'
                              value={translation.title}
                              onChange={(e) => {
                                const newTranslations = [...formik.values.translations]
                                newTranslations[index].title = e.target.value
                                formik.setFieldValue('translations', newTranslations)
                              }}
                            />
                          </div>
                          <div className='mb-5'>
                            <label className='form-label required'>{intl.formatMessage({id: 'TOUR_TYPE.TRANSLATION_DESCRIPTION'})}</label>
                            <textarea
                              className='form-control'
                              rows={2}
                              value={translation.description}
                              onChange={(e) => {
                                const newTranslations = [...formik.values.translations]
                                newTranslations[index].description = e.target.value
                                formik.setFieldValue('translations', newTranslations)
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                  <button
                    type='button'
                    className='btn btn-light-primary fw-semibold px-6'
                    onClick={handleCancel}
                    disabled={formik.isSubmitting || isDeleting}
                  >
                    {intl.formatMessage({id: 'ACTION.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary fw-semibold px-8'
                    disabled={formik.isSubmitting || isDeleting}
                  >
                    {formik.isSubmitting ? (
                      <>
                        <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                        <span className='ms-2'>{intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}</span>
                      </>
                    ) : (
                      intl.formatMessage({id: editingTourType ? 'ACTION.UPDATE' : 'ACTION.ADD'})
                    )}
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(15, 23, 42, 0.45)', zIndex: 1050}}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content border-0 rounded-4 shadow-lg'>
              <div className='modal-header border-0 pt-6 px-8 pb-2'>
                <h5 className='modal-title fs-3 fw-bold text-gray-900'>{intl.formatMessage({id: 'TOUR_TYPE.DELETE_CONFIRM'})}</h5>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  <i className='bi bi-x-lg fs-3' />
                </button>
              </div>
              <div className='modal-body px-8 py-6'>
                {intl.formatMessage({id: 'TOUR_TYPE.DELETE_WARNING'})}
              </div>
              <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                <button
                  type='button'
                  className='btn btn-light-primary fw-semibold px-6'
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  {intl.formatMessage({id: 'ACTION.CANCEL'})}
                </button>
                <button
                  type='button'
                  className='btn btn-danger fw-semibold px-6'
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                      <span className='ms-2'>{intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}</span>
                    </>
                  ) : (
                    intl.formatMessage({id: 'ACTION.DELETE'})
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TourTypesTab 